/**
 * Apparence du menu d'un restaurant.
 *
 * L'ecran vient de `apps/web-admin`, ou le restaurateur composait lui-meme sa
 * charte. Il a change de main : composer une charte n'est pas son metier, et
 * ce pouvoir n'a rien a faire dans l'application de salle. Voir
 * docs/adr/0026, qui amende l'ADR 0025.
 *
 * Le restaurant est designe par son identifiant : un administrateur de
 * plateforme n'a pas de restaurant courant (ADR 0009).
 */

import { useEffect, useState } from 'react';
import type {
  BrandPaletteDTO,
  PlatformRestaurantDTO,
  RestaurantDTO,
  UploadBrandingAssetResponse,
} from '@scan2eat/shared';
import { MENU_ITEM_IMAGE_MIME_TYPES } from '@scan2eat/shared';

import { ApiError, apiFetch } from '../api/client.js';
import { labels } from '../labels.js';
import { prepareImageForUpload } from '../lib/image.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { Icon } from './Icon.js';
import { Modal } from './Modal.js';
import styles from './BrandingDialog.module.css';

/**
 * Message le plus specifique disponible pour une erreur d'API : celui du
 * premier champ en faute -- le contraste insuffisant d'une couleur, par
 * exemple -- plutot que le generique « corps de la requete invalide ».
 */
function firstFieldErrorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.code !== 'VALIDATION_ERROR') return null;
  const details = error.details as { fieldErrors?: Record<string, string[]> } | null;
  const first = details?.fieldErrors && Object.values(details.fieldErrors)[0];
  return first?.[0] ?? null;
}

type SlotKind = 'logo' | 'room' | 'design';

const SLOTS: { kind: SlotKind; label: string; hint: string }[] = [
  { kind: 'logo', label: labels.branding.logoSlot, hint: labels.branding.logoHint },
  { kind: 'room', label: labels.branding.roomSlot, hint: labels.branding.roomHint },
  { kind: 'design', label: labels.branding.designSlot, hint: labels.branding.designHint },
];

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

const DEFAULT_PALETTE: BrandPaletteDTO = {
  primaryColor: '#824021',
  backgroundColor: '#FFFDF8',
  textColor: '#1B1B1B',
  accentColor: '#D97742',
};

function paletteOf(restaurant: {
  brandPrimaryColor: string | null;
  brandBackgroundColor: string | null;
  brandTextColor: string | null;
  brandAccentColor: string | null;
}): BrandPaletteDTO | null {
  const { brandPrimaryColor, brandBackgroundColor, brandTextColor, brandAccentColor } = restaurant;
  if (!brandPrimaryColor || !brandBackgroundColor || !brandTextColor || !brandAccentColor) return null;
  return {
    primaryColor: brandPrimaryColor,
    backgroundColor: brandBackgroundColor,
    textColor: brandTextColor,
    accentColor: brandAccentColor,
  };
}

function PaletteSwatches({ palette }: { palette: BrandPaletteDTO }) {
  return (
    <div className={styles.swatchRow}>
      {([palette.primaryColor, palette.backgroundColor, palette.textColor, palette.accentColor]).map(
        (hex, i) => (
          <span key={i} className={styles.swatch} style={{ background: hex }} title={hex} />
        ),
      )}
    </div>
  );
}

function PalettePreview({ palette }: { palette: BrandPaletteDTO }) {
  return (
    <div
      className={styles.preview}
      style={{ background: palette.backgroundColor, color: palette.textColor }}
    >
      <strong>{labels.branding.sampleText}</strong>
      <span
        className={styles.previewAccent}
        style={{ background: palette.accentColor, color: palette.backgroundColor }}
      >
        {labels.branding.sampleAccent}
      </span>
    </div>
  );
}

/** Pastille native et champ texte, synchronises : le selecteur seul ne permet
 *  pas de taper un code hexadecimal dans tous les navigateurs. */
function ColorField({ id, label, value, onChange }: {
  id: string; label: string; value: string; onChange: (hex: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  return (
    <div className="field">
      <label htmlFor={id} className="field-label">{label}</label>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} />
        <input
          type="text"
          className="input"
          value={draft}
          onChange={(e) => {
            const text = e.target.value;
            setDraft(text);
            if (HEX_COLOR_RE.test(text)) onChange(text);
          }}
          placeholder="#1B4332"
          maxLength={7}
          aria-label={label}
        />
      </div>
    </div>
  );
}

interface Props {
  restaurant: PlatformRestaurantDTO;
  onClose: () => void;
  /** Rafraichit la liste : la palette et les images vivent sur la fiche. */
  onChanged: () => Promise<void> | void;
}

export function BrandingDialog({ restaurant, onClose, onChanged }: Props) {
  const base = `/admin/restaurants/${restaurant.id}/branding`;

  const [palette, setPalette] = useState<BrandPaletteDTO | null>(paletteOf(restaurant));
  const [assetUrls, setAssetUrls] = useState<Partial<Record<SlotKind, string>>>({
    logo: restaurant.logoUrl ?? undefined,
    room: restaurant.roomPhotoUrl ?? undefined,
  });
  const [uploading, setUploading] = useState<SlotKind | null>(null);
  const [prompt, setPrompt] = useState('');
  const [proposals, setProposals] = useState<BrandPaletteDTO[]>([]);
  const [generating, setGenerating] = useState(false);
  const [applyingIndex, setApplyingIndex] = useState<number | null>(null);
  const [manualPalette, setManualPalette] = useState<BrandPaletteDTO>(palette ?? DEFAULT_PALETTE);
  const [manualApplying, setManualApplying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const busy = uploading !== null || generating || applyingIndex !== null || manualApplying || removing;

  /** Chaque ecriture rend le restaurant a jour : on suit sa palette. */
  const absorb = async (dto: RestaurantDTO, message: string) => {
    const next = paletteOf(dto);
    setPalette(next);
    if (next) setManualPalette(next);
    setAssetUrls({ logo: dto.logoUrl ?? undefined, room: dto.roomPhotoUrl ?? undefined });
    setBanner(message);
    setError(null);
    await onChanged();
  };

  const uploadAsset = async (kind: SlotKind, file: File) => {
    setUploading(kind);
    setError(null);
    try {
      const blob = await prepareImageForUpload(file);
      const result = await apiFetch<UploadBrandingAssetResponse>(`${base}/uploads/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': blob.type || file.type },
        body: blob,
      });
      setAssetUrls((prev) => ({ ...prev, [kind]: result.url }));
      // `design` n'est pas persiste : la reponse ne porte alors pas de fiche.
      if (result.restaurant) await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.branding.uploadFailed);
    } finally {
      setUploading(null);
    }
  };

  const generate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenerating(true);
    setError(null);
    setBanner(null);
    try {
      const response = await apiFetch<{ proposals: BrandPaletteDTO[] }>(`${base}/proposals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      setProposals(response.proposals);
    } catch (err) {
      setError(
        firstFieldErrorMessage(err)
          ?? (err instanceof ApiError ? err.message : labels.branding.generationFailed),
      );
    } finally {
      setGenerating(false);
    }
  };

  const apply = async (chosen: BrandPaletteDTO, index: number | null) => {
    if (index === null) setManualApplying(true);
    else setApplyingIndex(index);
    setError(null);
    try {
      const dto = await apiFetch<RestaurantDTO>(base, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(chosen),
      });
      await absorb(dto, labels.branding.applied);
      setProposals([]);
    } catch (err) {
      setError(
        firstFieldErrorMessage(err)
          ?? (err instanceof ApiError ? err.message : labels.auth.generic),
      );
    } finally {
      setApplyingIndex(null);
      setManualApplying(false);
    }
  };

  const remove = async () => {
    setConfirmingRemove(false);
    setRemoving(true);
    setError(null);
    try {
      const dto = await apiFetch<RestaurantDTO>(base, { method: 'DELETE' });
      await absorb(dto, labels.branding.removed);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : labels.auth.generic);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Modal
        title={labels.branding.title}
        description={labels.branding.subtitle(restaurant.name)}
        size="wide"
        onClose={onClose}
        locked={busy}
        footer={
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            {labels.branding.close}
          </button>
        }
      >
        <div className="form-grid">
          {error && (
            <p role="alert" className="notice notice--danger" style={{ margin: 0 }}>
              <Icon name="alert" size={18} />
              <span className="grow">{error}</span>
            </p>
          )}
          {banner && (
            <p role="status" className="notice notice--success" style={{ margin: 0 }}>
              <Icon name="check" size={18} />
              <span className="grow">{banner}</span>
            </p>
          )}

          <section className={styles.block}>
            <h3 className={styles.blockTitle}>{labels.branding.currentSection}</h3>
            {palette ? (
              <>
                <PalettePreview palette={palette} />
                <div className={styles.currentFoot}>
                  <PaletteSwatches palette={palette} />
                  <button
                    type="button"
                    className="btn-danger btn-sm"
                    onClick={() => setConfirmingRemove(true)}
                    disabled={busy}
                  >
                    {removing ? labels.branding.removing : labels.branding.removePalette}
                  </button>
                </div>
              </>
            ) : (
              <p className="field-hint">{labels.branding.noPalette}</p>
            )}
          </section>

          <form onSubmit={(e) => void generate(e)} className={styles.block}>
            <h3 className={styles.blockTitle}>{labels.branding.generateSection}</h3>
            <p className="field-hint">{labels.branding.generateHelp}</p>

            <div className="field">
              <label htmlFor="branding-prompt" className="field-label">
                {labels.branding.promptLabel}
              </label>
              <textarea
                id="branding-prompt"
                className="input"
                rows={3}
                maxLength={500}
                placeholder={labels.branding.promptPlaceholder}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                required
              />
            </div>

            <div className={styles.uploadGrid}>
              {SLOTS.map((slot) => (
                <div key={slot.kind} className={styles.uploadSlot}>
                  <label className={styles.dropzone}>
                    {assetUrls[slot.kind] ? (
                      <img src={assetUrls[slot.kind]} alt={slot.label} />
                    ) : uploading === slot.kind ? (
                      <Icon name="refresh" />
                    ) : (
                      <Icon name="image" />
                    )}
                    <input
                      type="file"
                      accept={MENU_ITEM_IMAGE_MIME_TYPES.join(',')}
                      hidden
                      disabled={busy}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void uploadAsset(slot.kind, file);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <span className="field-hint">{slot.label}</span>
                  <span className="field-hint">{slot.hint}</span>
                </div>
              ))}
            </div>

            <button type="submit" className="btn-primary" disabled={busy || prompt.trim().length === 0}>
              {generating ? labels.branding.generating : labels.branding.generate}
            </button>
          </form>

          {proposals.length > 0 && (
            <section className={styles.block}>
              <h3 className={styles.blockTitle}>{labels.branding.proposalsTitle}</h3>
              <div className={styles.proposals}>
                {proposals.map((proposal, index) => (
                  <div key={index} className={styles.proposalCard}>
                    <PalettePreview palette={proposal} />
                    <PaletteSwatches palette={proposal} />
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      disabled={busy}
                      onClick={() => void apply(proposal, index)}
                    >
                      {applyingIndex === index ? labels.branding.applying : labels.branding.apply}
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className={styles.block}>
            <h3 className={styles.blockTitle}>{labels.branding.manualSection}</h3>
            <p className="field-hint">{labels.branding.manualHelp}</p>
            <PalettePreview palette={manualPalette} />
            <div className="form-grid form-grid--2">
              {(
                [
                  ['primaryColor', labels.branding.primaryColor],
                  ['backgroundColor', labels.branding.backgroundColor],
                  ['textColor', labels.branding.textColor],
                  ['accentColor', labels.branding.accentColor],
                ] as const
              ).map(([key, fieldLabel]) => (
                <ColorField
                  key={key}
                  id={`manual-${key}`}
                  label={fieldLabel}
                  value={manualPalette[key]}
                  onChange={(hex) => setManualPalette((prev) => ({ ...prev, [key]: hex }))}
                />
              ))}
            </div>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => void apply(manualPalette, null)}
            >
              {manualApplying ? labels.branding.applying : labels.branding.applyManual}
            </button>
          </section>
        </div>
      </Modal>

      {confirmingRemove && (
        <ConfirmDialog
          title={labels.branding.removeTitle}
          message={labels.branding.removeMessage}
          confirmLabel={labels.branding.removePalette}
          destructive
          onConfirm={() => void remove()}
          onCancel={() => setConfirmingRemove(false)}
        />
      )}
    </>
  );
}
